// components/ProductManager.js
import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit2, Trash2, Save, X, Search, Filter, 
  Upload, Download, Grid, List 
} from 'lucide-react';
import { products as initialProducts, productCategories } from '../data/products';
import ExcelUploader from './ExcelUploader';

const ProductManager = ({ onClose }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [editingId, setEditingId] = useState(null);
  const [editingProduct, setEditingProduct] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showExcelUploader, setShowExcelUploader] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: productCategories[0],
    unitPrice: '',
    unit: '개',
    description: ''
  });

  // localStorage에서 제품 목록 불러오기
  useEffect(() => {
    const savedProducts = localStorage.getItem('productList');
    if (savedProducts) {
      setProducts(JSON.parse(savedProducts));
    } else {
      setProducts(initialProducts);
      localStorage.setItem('productList', JSON.stringify(initialProducts));
    }
  }, []);

  // 검색 및 필터링
  useEffect(() => {
    let filtered = products;

    // 카테고리 필터
    if (selectedCategory !== '전체') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // 검색 필터
    if (searchQuery.trim()) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategory]);

  // localStorage에 저장
  const saveToStorage = (productList) => {
    localStorage.setItem('productList', JSON.stringify(productList));
    setProducts(productList);
  };

  // 제품 추가
  const handleAddProduct = () => {
    if (!newProduct.name || !newProduct.unitPrice) {
      alert('품목명과 단가는 필수입니다.');
      return;
    }

    const product = {
      id: Date.now(),
      ...newProduct,
      unitPrice: parseFloat(newProduct.unitPrice)
    };

    const updatedProducts = [...products, product];
    saveToStorage(updatedProducts);
    setNewProduct({
      name: '',
      category: productCategories[0],
      unitPrice: '',
      unit: '개',
      description: ''
    });
    setShowAddForm(false);
    alert('제품이 추가되었습니다.');
  };

  // 제품 수정 시작
  const handleEditStart = (product) => {
    setEditingId(product.id);
    setEditingProduct({ ...product });
  };

  // 제품 수정 저장
  const handleEditSave = () => {
    if (!editingProduct.name || !editingProduct.unitPrice) {
      alert('품목명과 단가는 필수입니다.');
      return;
    }

    const updatedProducts = products.map(product =>
      product.id === editingId
        ? { ...editingProduct, unitPrice: parseFloat(editingProduct.unitPrice) }
        : product
    );

    saveToStorage(updatedProducts);
    setEditingId(null);
    setEditingProduct({});
    alert('제품이 수정되었습니다.');
  };

  // 제품 수정 취소
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingProduct({});
  };

  // 제품 삭제
  const handleDelete = (productId) => {
    if (window.confirm('정말로 이 제품을 삭제하시겠습니까?')) {
      const updatedProducts = products.filter(product => product.id !== productId);
      saveToStorage(updatedProducts);
      alert('제품이 삭제되었습니다.');
    }
  };

  // 엑셀 가져오기
  const handleProductsImported = (importedProducts) => {
    const updatedProducts = [...products, ...importedProducts];
    saveToStorage(updatedProducts);
    alert(`${importedProducts.length}개의 제품이 추가되었습니다.`);
  };

  // CSV 내보내기
  const handleExportCSV = () => {
    // UTF-8 BOM 추가 (엑셀에서 한글 깨짐 방지)
    const BOM = '\uFEFF';
    const csvContent = [
      '카테고리,품목명,단가,단위,설명',
      ...products.map(p => `"${p.category}","${p.name}","${p.unitPrice}","${p.unit}","${p.description}"`)
    ].join('\n');

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '품목 관리 목록.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const ProductCard = ({ product }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {editingId === product.id ? (
        <div className="space-y-3">
          <input
            type="text"
            value={editingProduct.name || ''}
            onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="품목명"
          />
          <select
            value={editingProduct.category || ''}
            onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {productCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <input
            type="number"
            value={editingProduct.unitPrice || ''}
            onChange={(e) => setEditingProduct({...editingProduct, unitPrice: e.target.value})}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="단가"
          />
          <input
            type="text"
            value={editingProduct.unit || ''}
            onChange={(e) => setEditingProduct({...editingProduct, unit: e.target.value})}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="단위"
          />
          <textarea
            value={editingProduct.description || ''}
            onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            placeholder="설명"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              onClick={handleEditSave}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
            >
              <Save className="w-3 h-3" />
              저장
            </button>
            <button
              onClick={handleEditCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              취소
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
            <div className="flex gap-1 ml-2">
              <button
                onClick={() => handleEditStart(product)}
                className="text-blue-500 hover:text-blue-700 p-1"
                title="수정"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(product.id)}
                className="text-red-500 hover:text-red-700 p-1"
                title="삭제"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500 mb-1">{product.category}</div>
          <div className="text-lg font-semibold text-blue-600 mb-2">
            {product.unitPrice.toLocaleString()}원/{product.unit}
          </div>
          <div className="text-xs text-gray-400 truncate">{product.description}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Package className="w-6 h-6 mr-2" />
              품목 관리
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              총 {products.length}개 품목 (표시: {filteredProducts.length}개)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 툴바 */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                품목 추가
              </button>
              <button
                onClick={() => setShowExcelUploader(true)}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                일괄 업로드
              </button>
              <button
                onClick={handleExportCSV}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                CSV 내보내기
              </button>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm"
              >
                {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* 검색 및 필터 */}
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="품목명 또는 설명으로 검색..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="min-w-40">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="전체">전체 카테고리</option>
                {productCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 제품 추가 폼 */}
        {showAddForm && (
          <div className="p-4 bg-blue-50 border-b border-blue-200">
            <h3 className="text-lg font-medium mb-3">새 품목 추가</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="품목명 *"
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={newProduct.category}
                onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {productCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="number"
                value={newProduct.unitPrice}
                onChange={(e) => setNewProduct({...newProduct, unitPrice: e.target.value})}
                placeholder="단가 *"
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={newProduct.unit}
                onChange={(e) => setNewProduct({...newProduct, unit: e.target.value})}
                placeholder="단위"
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                placeholder="설명"
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAddProduct}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
              >
                추가
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
              >
                취소
              </button>
            </div>
          </div>
        )}

        {/* 제품 목록 */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>품목이 없습니다.</p>
              {searchQuery || selectedCategory !== '전체' ? (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('전체');
                  }}
                  className="text-blue-500 hover:text-blue-700 text-sm mt-2"
                >
                  필터 초기화
                </button>
              ) : null}
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              : 'space-y-2'
            }>
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>

        {/* 엑셀 업로더 모달 */}
        {showExcelUploader && (
          <ExcelUploader
            onProductsImported={handleProductsImported}
            onClose={() => setShowExcelUploader(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ProductManager;